(() => {
  'use strict';
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const select = (selector, root = document) => root.querySelector(selector);
  const all = (selector, root = document) => [...root.querySelectorAll(selector)];

  // Use native disclosures only on mobile, retaining the full desktop rows.
  const mobileValues = matchMedia('(max-width:700px)');
  const accordionGroups = [
    {rows:all('.energy-section .value'), number:'.value-num', heading:'h4', className:'value-accordion', desktopTag:'div', wrapCopy:true, open:new Set([0])},
    {rows:all('.page-index .start-section .step'), number:':scope > span, :scope > summary > span', heading:'h3', className:'step-accordion', desktopTag:'article', wrapCopy:false, open:new Set([0])},
    {rows:all('.page-hyrox .hx-waymore-grid article'), number:'.hx-line-num', heading:'h3', className:'hx-waymore-accordion', desktopTag:'article', wrapCopy:false, open:new Set([0])}
  ];
  function arrangeValues() {
    accordionGroups.forEach(group => group.rows.forEach((row, index) => {
      const mobile = mobileValues.matches;
      if (mobile === (row.tagName === 'DETAILS')) return;
      const number = select(group.number, row);
      const heading = select(group.heading, row);
      const description = select('p', row);
      const replacement = document.createElement(mobile ? 'details' : group.desktopTag);
      [...row.attributes].forEach(attribute => replacement.setAttribute(attribute.name, attribute.value));
      replacement.classList.toggle(group.className, mobile);
      // A resized row may replace an element its reveal observer was tracking.
      replacement.classList.remove('is-pending');
      replacement.removeAttribute('open');
      if (mobile) {
        const summary = document.createElement('summary');
        summary.append(number, heading);
        replacement.append(summary, description);
        replacement.open = group.open.has(index);
        replacement.addEventListener('toggle', () => {
          if (!replacement.isConnected) return;
          if (replacement.open) group.open.add(index);
          else group.open.delete(index);
        });
      } else if (group.wrapCopy) {
        const copy = document.createElement('div');
        copy.append(heading, description);
        replacement.append(number, copy);
      } else {
        replacement.append(number, heading, description);
      }
      row.replaceWith(replacement);
      group.rows[index] = replacement;
    }));
  }
  arrangeValues();
  mobileValues.addEventListener('change', arrangeValues);

  // Keep all content readable if scripts, observers, or motion are unavailable.
  if ('IntersectionObserver' in window && !reduced.matches) {
    const targets = all('.section-head > div, .section-head > p, .program-card, .energy-film, .energy-section .experience-copy > .eyebrow, .energy-section .experience-copy > h2, .energy-section .experience-copy > p, .value, .reviews-heading, .location-feature, .step, .membership-teaser, .faq-list details, .program-detail, .plan-card, .page-image, .cta-section h2');
    targets.forEach(element => {
      element.dataset.reveal ||= 'rise';
      element.classList.add('is-pending');
      if (element.matches('.value, .step, .program-card')) {
        const index = [...element.parentElement.children].indexOf(element);
        element.style.setProperty('--reveal-delay', `${Math.min(index, 2) * 105}ms`);
      }
    });
    document.documentElement.classList.add('motion-ready');
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        entry.target.classList.remove('is-pending');
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    }, {threshold: .12, rootMargin: '0px 0px -25px 0px'});
    targets.forEach(element => observer.observe(element));
    reduced.addEventListener('change', () => {
      if (!reduced.matches) return;
      observer.disconnect();
      targets.forEach(element => element.classList.remove('is-pending'));
    });
    // Focusing a link must never leave it behind an unfinished reveal.
    document.addEventListener('focusin', event => {
      const reveal = event.target.closest('[data-reveal]');
      reveal?.classList.remove('is-pending');
    });
  }


  // Begin the gym film when it is half visible; native controls remain in charge.
  const gymFilm = select('.gym-reel-video');
  if (gymFilm && 'IntersectionObserver' in window) {
    let inView = false;
    let resumeAfterHidden = false;
    const startFilm = () => {
      if (!inView || document.hidden || reduced.matches) return;
      gymFilm.play().then(() => {
        // Loading may finish after the visitor has already scrolled away.
        if (!inView || document.hidden || reduced.matches) gymFilm.pause();
      }).catch(() => { /* Keep the native play button available if autoplay is blocked. */ });
    };
    const filmObserver = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (!entry.isIntersecting) {
        inView = false;
        resumeAfterHidden = false;
        gymFilm.pause();
      } else if (entry.intersectionRatio >= .5 && !inView) {
        inView = true;
        startFilm();
      }
    }, {threshold: [0, .5], rootMargin: '-100px 0px 0px 0px'});
    filmObserver.observe(gymFilm);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        resumeAfterHidden = inView && !gymFilm.paused;
        gymFilm.pause();
      } else if (resumeAfterHidden) {
        resumeAfterHidden = false;
        startFilm();
      }
    });
    reduced.addEventListener('change', () => {
      if (reduced.matches) gymFilm.pause();
      else startFilm();
    });
  }

  // Start each numbered sequence when 01 is visible; reset after leaving it.
  const numberGroups = all('.start-section .steps');
  if ('IntersectionObserver' in window && numberGroups.length) {
    const firstNumbers = new Map();
    numberGroups.forEach(section => {
      const numbers = all('.step > span, .step > summary > span', section);
      numbers.forEach((number, index) => {
        number.classList.add('sequence-number');
        number.style.setProperty('--pulse-delay', (350 + index * 1100) + 'ms');
      });
      if (numbers[0]) firstNumbers.set(numbers[0], section);
    });
    const startObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !reduced.matches) {
          firstNumbers.get(entry.target).classList.add('is-number-pulsing');
        }
      });
    }, {threshold:.7, rootMargin:'-105px 0px -35px 0px'});
    const exitObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) entry.target.classList.remove('is-number-pulsing');
      });
    });
    firstNumbers.forEach((section, number) => startObserver.observe(number));
    numberGroups.forEach(section => exitObserver.observe(section));
    const pauseNumbers = () => numberGroups.forEach(section => {
      section.classList.toggle('is-number-paused', document.hidden);
    });
    document.addEventListener('visibilitychange', pauseNumbers);
    pauseNumbers();
    reduced.addEventListener('change', () => {
      numberGroups.forEach(section => section.classList.remove('is-number-pulsing'));
      if (!reduced.matches) firstNumbers.forEach((section, number) => {
        startObserver.unobserve(number);
        startObserver.observe(number);
      });
    });
  }

  // The mobile feature strip loops as one row; desktop retains its four columns.
  const trustStrip = select('.trust-strip');
  const trustGroup = select('.trust-inner');
  if (trustStrip && trustGroup) {
    const trustTrack = document.createElement('div');
    trustTrack.className = 'trust-loop';
    trustGroup.before(trustTrack);
    trustTrack.append(trustGroup);
    const copy = trustGroup.cloneNode(true);
    copy.dataset.trustCopy = '';
    copy.setAttribute('aria-hidden', 'true');
    copy.setAttribute('inert', '');
    // Keep the wordmark's SVG clipping references unique in the duplicate.
    all('[id]', copy).forEach(element => {
      const oldId = element.id;
      element.id = oldId + '-trust-copy';
      all('[clip-path]', copy).forEach(clipped => {
        if (clipped.getAttribute('clip-path') === `url(#${oldId})`) {
          clipped.setAttribute('clip-path', `url(#${element.id})`);
        }
      });
    });
    trustTrack.append(copy);
    const trustVisibility = () => trustStrip.classList.toggle('is-tab-hidden', document.hidden);
    document.addEventListener('visibilitychange', trustVisibility);
    trustVisibility();
    if ('IntersectionObserver' in window) new IntersectionObserver(([entry]) => {
      trustStrip.classList.toggle('is-offscreen', !entry.isIntersecting);
    }).observe(trustStrip);
  }

  const viewport = select('#reviews-viewport');
  const track = select('#reviews-track');
  const group = select('.reviews-group', track || document);
  if (!viewport || !track || !group) return;
  const cards = () => all('.review-card', group);

  // One complete duplicate makes the last card flow directly into the first.
  // Only the original set is exposed to assistive technology.
  function prepareLoop() {
    all('[data-review-copy]', track).forEach(copy => copy.remove());
    const copy = group.cloneNode(true);
    copy.dataset.reviewCopy = '';
    copy.setAttribute('aria-hidden', 'true');
    copy.setAttribute('inert', '');
    all('[id]', copy).forEach(element => element.removeAttribute('id'));
    track.append(copy);
    sizeLoop();
  }
  function sizeLoop() {
    const width = viewport.clientWidth;
    const narrow = innerWidth <= 700;
    const columns = innerWidth <= 900 ? 2 : 3;
    const gap = narrow ? 13 : 18;
    const cardWidth = narrow ? Math.min(280, width * .88) : (width - gap * (columns - 1)) / columns;
    viewport.style.setProperty('--review-card-width', cardWidth + 'px');
    viewport.style.setProperty('--review-gap', gap + 'px');
    const distance = group.getBoundingClientRect().width + gap;
    viewport.style.setProperty('--review-loop-distance', distance + 'px');
    viewport.style.setProperty('--review-loop-duration', distance / 28 + 's');
    viewport.classList.toggle('has-loop', cards().length > columns);
  }
  function visibility() {
    viewport.classList.toggle('is-tab-hidden', document.hidden);
  }
  document.addEventListener('visibilitychange', visibility);
  if ('IntersectionObserver' in window) new IntersectionObserver(([entry]) => {
    viewport.classList.toggle('is-offscreen', !entry.isIntersecting);
  }, {threshold:0}).observe(viewport);
  if ('ResizeObserver' in window) new ResizeObserver(sizeLoop).observe(viewport);
  else window.addEventListener('resize', sizeLoop, {passive:true});
  reduced.addEventListener('change', sizeLoop);
  prepareLoop(); visibility();


  // Optional owner-authorized feed; static, attributed excerpts remain the fallback.
  const endpoint = window.WAYMORE_CONFIG.reviewsFeedEndpoint;
  if (endpoint) (async () => {
    try {
      const url = new URL(endpoint, location.href);
      if (url.origin !== location.origin) throw new Error('Reviews require a same-origin endpoint');
      const response = await fetch(url, {signal:AbortSignal.timeout(6000)});
      if (!response.ok) throw new Error('Review feed unavailable');
      const data = await response.json();
      const safeGoogleURL = value => {
        try { const u = new URL(value); return u.protocol === 'https:' && (u.hostname === 'www.google.com' || u.hostname === 'google.com' || u.hostname === 'maps.app.goo.gl'); }
        catch { return false; }
      };
      const reviews = data.reviews?.filter(r => typeof r.author === 'string' && r.author.length <= 100 && typeof r.quote === 'string' && r.quote.length <= 600 && Number.isInteger(r.rating) && r.rating >= 1 && r.rating <= 5 && safeGoogleURL(r.url)).slice(0,12);
      if (!reviews?.length || !safeGoogleURL(data.sourceUrl) || !/^\d{4}-\d{2}-\d{2}$/.test(data.checkedAt)) throw new Error('Invalid review data');
      const date = new Date(`${data.checkedAt}T12:00:00`);
      if (Number.isNaN(date.getTime())) throw new Error('Invalid review date');
      const fragment = document.createDocumentFragment();
      reviews.forEach(review => {
        const card = cards()[0].cloneNode(true);
        select('blockquote',card).textContent = `“${review.quote}”`;
        select('.review-person b',card).textContent = review.author;
        select('.review-avatar',card).textContent = review.author.split(/\s+/).slice(0,2).map(n=>n[0]).join('');
        select('.review-topic',card).textContent = typeof review.topic === 'string' ? review.topic.slice(0,40) : 'Member experience';
        select('.stars',card).textContent = '★'.repeat(review.rating);
        select('.stars',card).setAttribute('aria-label', `${review.rating} out of 5 stars`);
        fragment.append(card);
      });
      group.replaceChildren(fragment);
      if (Number.isFinite(data.rating) && data.rating >= 1 && data.rating <= 5 && Number.isInteger(data.count) && data.count >= reviews.length) {
        select('#review-rating').textContent = data.rating.toFixed(1);
        select('#review-count').textContent = `${data.count} Google reviews`;
        select('.review-score .stars').setAttribute('aria-label', `${data.rating} out of 5 stars`);
      }
      select('.review-score').href = data.sourceUrl;
      select('#review-note').textContent = `Selected excerpts · Google profile checked ${date.toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'})}`;
      prepareLoop();
    } catch { /* The verified static reviews remain visible. */ }
  })();
})();
